/* Макет тура. Слои лежат друг на друге и переключаются шагом: список поездок,
   редактор поездки и окна поверх него — источник, доступ, публикация.

   Всё движение описано здесь и завязано на состояния слоёв, которые
   раскладывает stageView() (data-trips, data-editor, data-fill, data-window):
   анимация не должна зависеть от таймеров в JS, иначе она рано или поздно
   разъедется с шагом, а селектор по номеру шага пришлось бы переписывать
   каждый раз, когда шаг вставляют в середину.
   Числа макета (высота сцены, высота колонки карточек, длина прокрутки)
   собраны переменными наверху — они подогнаны друг к другу и меняются вместе. */

.stage {
  /* Ход у сцены длиннее рабочего --dnd-dur: там физика перетаскивания, здесь
     показ. Собственная кривая — чтобы движение замедлялось к концу. */
  --tour-ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --tour-hand: 1700ms; /* пересборка экрана на втором шаге */
  /* Высота ленты карточек: с ней итоги обеих колонок встают на одну строку,
     хотя строк в колонках разное количество. */
  --tour-cards-h: 180px;

  position: relative;
  height: 320px;
  overflow: hidden;
  background: var(--bg);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
  /* Сцену не выделяют и не таскают: это картинка, а не рабочая область. */
  user-select: none;
}

.layer {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  opacity: 0;
}

/* ── Слой 1: список поездок ─────────────────────────────────────────────── */

/* Список короткий, а сцена одной высоты на всех шагах — поэтому он стоит по
   центру, а не жмётся к верхнему краю с пустотой под собой. */
.trips {
  justify-content: center;
  gap: var(--sp-5);
  padding: var(--sp-6);
}

.stage[data-trips='shown'] .trips {
  opacity: 1;
}

.stage[data-trips='leaving'] .trips {
  animation: handoffOut var(--tour-hand) var(--tour-ease) both;
}

.tripsHead {
  font-size: var(--fs-lg);
  font-weight: 600;
  color: var(--text);
}

.tripList {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  margin: 0;
  padding: 0;
  list-style: none;
}

.tripRow {
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-4) var(--sp-5);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius-lg);
}

/* Строки выезжают по очереди — так видно, что поездок именно три и они
   равноправны, а не одна с двумя приписками. */
.stage[data-trips='shown'] .tripRow {
  animation: rise 460ms var(--tour-ease) both;
  animation-delay: calc(var(--i) * 130ms + 140ms);
}

.tripIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  color: var(--flight);
}

.tripName {
  font-size: var(--fs-md);
  font-weight: 600;
  color: var(--text);
}

.tripRoute {
  color: var(--text-3);
  font-size: var(--fs-xs);
  letter-spacing: 0.04em;
}

.tripOpen {
  margin-left: auto;
  padding: 1px var(--sp-3);
  border: var(--bw) solid var(--border-strong);
  border-radius: var(--radius);
  color: var(--text-2);
  font-size: var(--fs-xs);
}

/* Шаблоны под списком: на настоящей странице это свёрнутый раздел, здесь —
   строка с тремя названиями. Приезжает последней, после самих поездок. */
.templates {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-4);
  padding-top: var(--sp-2);
}

.stage[data-trips='shown'] .templates {
  animation: rise 460ms var(--tour-ease) both;
  animation-delay: calc(var(--i) * 130ms + 140ms);
}

.templatesTitle {
  color: var(--text-2);
  font-size: var(--fs-sm);
  font-weight: 600;
}

.chipRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  min-width: 0;
}

.chip {
  padding: 1px var(--sp-3);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  color: var(--text-3);
  font-size: var(--fs-xs);
  white-space: nowrap;
}

/* Второй шаг: Европу подсвечивает кольцо фокуса и короткое нажатие — курсора
   в макете нет, и выбор нужно показать самой строкой. */
.stage[data-trips='leaving'] .tripPicked {
  animation: pick var(--tour-hand) var(--tour-ease) both;
}

/* ── Слой 2: редактирование поездки ─────────────────────────────────────── */

.stage[data-editor='entering'] .editor {
  animation: handoffIn var(--tour-hand) var(--tour-ease) both;
}

.stage[data-editor='shown'] .editor {
  opacity: 1;
}

.bar {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex: none;
  padding: var(--sp-3) var(--sp-4);
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}

.barBrand {
  font-family: var(--font-brand);
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--brand);
  white-space: nowrap;
}

/* Контролы полосы в макете не нужны поимённо: они обозначают, что полоса есть,
   и не спорят за внимание с доской ниже. */
.barChip {
  width: 26px;
  height: 8px;
  flex: none;
  border-radius: var(--radius);
  background: var(--surface-2);
  border: var(--bw) solid var(--border);
}

.barChipWide {
  width: 42px;
}

.barSpacer {
  flex: 1 1 auto;
}

.cols {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
}

/* Ширины хватает на подпись частого блока целиком: «Аэропорт → Город» в
   многоточии не читается, а именно эти подписи и объясняют, что такое блок. */
.lib {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  flex: none;
  width: 136px;
  padding: var(--sp-4);
  background: var(--surface);
  border-right: 1px solid var(--border);
}

.libHead {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
}

.libSection {
  color: var(--text-3);
  font-size: var(--fs-xs);
}

/* Чипс частого блока — как в библиотеке: иконка типа, подпись, кратность. */
.libRow {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  background: var(--surface-2);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  color: var(--text-2);
}

.libLabel {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--fs-xs);
}

.libCount {
  flex: none;
  color: var(--text-3);
  font-size: var(--fs-xs);
}

.libCount:empty {
  display: none;
}

.board {
  flex: 1 1 auto;
  min-width: 0;
  padding: var(--sp-4);
  overflow: hidden;
}

/* Колонки во всю высоту доски и одной высоты между собой — так их итоги
   встают на одну строку и сравниваются одним взглядом. */
.boardCols {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: var(--sp-4);
  height: 100%;
}

.variant {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--surface-2);
  /* Та же цветная обводка колонки, что и на настоящей доске: варианты
     различаются цветом, а не только названием. */
  border: max(2px, var(--bw)) solid var(--variant-color, var(--border-strong));
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.variantHead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2) var(--sp-3);
  flex: none;
  padding: var(--sp-3) var(--sp-4);
  border-bottom: 1px solid var(--border);
}

/* Окно ленты: содержимое выше него, поэтому четвёртому шагу есть куда ехать.
   Уезжает только оно — шапка с названием варианта остаётся на месте, иначе на
   последнем шаге две суммы висят без имён и сравнивать нечего.

   Полосы прокрутки нет (overflow: hidden), но scrollTop работает и без неё:
   ленту двигает сам шаг (см. Stage.tsx), руками её не трогают. */
.variantWindow {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  scroll-behavior: smooth;
}

.dot {
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: 50%;
  background: var(--variant-color, var(--border-strong));
}

.variantName {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
}

/* Подпись занимает всю строку под названием: в ней вся разница между
   вариантами, и обрезать её многоточием нельзя.

   Пока колонки пусты, подписи нет: «2 ночёвки в пути» над пустым вариантом
   рассказывала бы о блоках, которых человек ещё не видел. */
.variantNote {
  width: 100%;
  color: var(--text-3);
  font-size: var(--fs-xs);
  opacity: 0;
}

.stage:not([data-fill='empty']) .variantNote {
  opacity: 1;
  transition: opacity 420ms var(--tour-ease) 260ms;
}

.cards {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  /* Ленты обеих колонок одной высоты — иначе итоги встают на разной строке
     и сравнивать глазами нечего. */
  min-height: var(--tour-cards-h);
  margin: 0;
  padding: var(--sp-3);
  list-style: none;
}

.card {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  background: var(--surface);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  /* На втором шаге поездка только открыта: колонки пустые, карточки
     появляются на третьем. */
  opacity: 0;
}

.stage[data-fill='entering'] .card {
  animation: rise 380ms var(--tour-ease) both;
  animation-delay: calc(var(--i) * 100ms + 260ms);
}

.stage[data-fill='filled'] .card {
  opacity: 1;
}

.cardIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid currentColor;
}

.flightIcon {
  color: var(--flight);
}

.lodgingIcon {
  color: var(--lodging);
}

.cardTitle {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--fs-xs);
  color: var(--text);
}

.cardRoute {
  font-weight: 600;
  letter-spacing: 0.04em;
}

.cardPrice {
  flex: none;
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--text);
}

/* Корешок варианта — как в настоящих итогах: светлее колонки и отделён
   линией отрыва. */
.totals {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: var(--sp-3) var(--sp-4) var(--sp-4);
  background: var(--surface-raised);
  border-top: 1px dashed var(--border-strong);
  opacity: 0;
}

.stage[data-fill='entering'] .totals {
  animation: fade 420ms var(--tour-ease) 1150ms both;
}

.stage[data-fill='filled'] .totals {
  opacity: 1;
}

.totalLine {
  display: flex;
  justify-content: space-between;
  gap: var(--sp-3);
  color: var(--text-3);
  font-size: var(--fs-xs);
}

.totalSum {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--sp-3);
  margin-top: var(--sp-2);
  padding-top: var(--sp-2);
  border-top: 1px solid var(--border);
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
}

.totalLabel {
  flex: none;
}

.totalValue {
  margin-left: auto;
  font-size: var(--fs-lg);
  font-weight: 700;
}

/* Итог победителя зеленеет только на четвёртом шаге: до прокрутки сравнивать
   ещё нечего, и подсказывать ответ рано. */
.stage[data-fill='filled'] .variantWin .totalValue {
  color: var(--ok);
}

/* Бейдж стоит в одной строке с итогом, а не под ним: отдельной строкой он
   вылезал за нижний край сцены ровно на тот шаг, ради которого нужен. */
.win {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 1px var(--sp-3);
  border: var(--bw) solid var(--ok);
  border-radius: var(--radius);
  color: var(--ok);
  font-size: var(--fs-xs);
  font-weight: 400;
  opacity: 0;
}

.stage[data-fill='filled'] .variantWin .win {
  animation: pop 420ms var(--tour-ease) 820ms both;
}

/* Место занимает, но не показывается: сравнивают суммы, а не пустые рамки. */
.winGhost {
  visibility: hidden;
}

/* ── Окна поверх редактора: источник, доступ, публикация ────────────────
   Все три — одна и та же рамка над той же доской. Редактор под ними остаётся
   виден и притушен: важно, что это окно внутри поездки, а не отдельный экран,
   на который человека увели. */

.windowWrap {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-5);
  background: color-mix(in srgb, var(--overlay) 55%, transparent);
  animation: fade 320ms var(--tour-ease) both;
}

.window {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  width: min(360px, 100%);
  max-height: 100%;
  overflow: hidden;
  padding: var(--sp-5);
  background: var(--surface);
  border: var(--bw) solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-modal);
  animation: popIn 420ms var(--tour-ease) 120ms both;
}

.windowTitle {
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
}

.windowHeadRow {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-3);
}

.windowNote {
  color: var(--text-3);
  font-size: var(--fs-xs);
}

/* Вкладки распознавания: описание, ссылка, скриншот. */
.tabs {
  display: flex;
  gap: var(--sp-2);
  border-bottom: 1px solid var(--border);
}

.tab {
  padding: var(--sp-2) var(--sp-3);
  border-bottom: 2px solid transparent;
  color: var(--text-3);
  font-size: var(--fs-xs);
}

.tabActive {
  border-bottom-color: var(--focus);
  color: var(--text);
  font-weight: 600;
}

.fieldLabel {
  color: var(--text-2);
  font-size: var(--fs-xs);
  font-weight: 500;
}

.fieldRow {
  display: flex;
  align-items: stretch;
  gap: var(--sp-3);
}

.field {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: var(--sp-2) var(--sp-3);
  background: var(--surface);
  border: var(--bw) solid var(--border-strong);
  border-radius: var(--radius);
  color: var(--text-2);
  font-size: var(--fs-xs);
}

.fieldBtn {
  flex: none;
  padding: var(--sp-2) var(--sp-4);
  background: var(--btn-primary-bg);
  border: var(--bw) solid var(--btn-primary-bg);
  border-radius: var(--radius);
  color: var(--btn-primary-fg);
  font-size: var(--fs-xs);
  white-space: nowrap;
}

/* Черновик, который вернуло распознавание: та же карточка, что ляжет
   в библиотеку, — иначе связь между окном и результатом теряется. */
.draft {
  opacity: 1;
  animation: rise 420ms var(--tour-ease) 620ms both;
}

.people {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.person {
  display: flex;
  align-items: baseline;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  background: var(--surface-2);
  border: var(--bw) solid var(--border);
  border-radius: var(--radius);
  font-size: var(--fs-xs);
}

/* Приглашённый появляется последним: строка и есть результат нажатия. */
.personNew {
  animation: rise 420ms var(--tour-ease) 700ms both;
}

.personName {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.personYou {
  flex: none;
  color: var(--text-3);
}

.personRole {
  flex: none;
  color: var(--text-2);
}

.readonly {
  flex: none;
  padding: 1px var(--sp-3);
  border: var(--bw) solid var(--border-strong);
  border-radius: var(--radius);
  color: var(--text-3);
  font-size: var(--fs-xs);
  white-space: nowrap;
}

/* ── Движение ───────────────────────────────────────────────────────────── */

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes pop {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  60% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

/* Половину шага список поездок стоит на месте — за это время успевает
   отработать подсветка Европы, — и только потом уступает редактору. */
@keyframes handoffOut {
  0%,
  52% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.965);
  }
}

@keyframes handoffIn {
  0%,
  46% {
    opacity: 0;
    transform: scale(1.035);
  }
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes pick {
  0% {
    border-color: var(--border);
    box-shadow: none;
  }
  20% {
    border-color: var(--focus);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--focus) 30%, transparent);
    transform: none;
  }
  34% {
    transform: scale(0.98);
  }
  48%,
  100% {
    border-color: var(--focus);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--focus) 30%, transparent);
    transform: none;
  }
}

/* Движение выключено — но не содержимое: каждый шаг обязан показывать своё
   конечное состояние, иначе второй остался бы пустым экраном. */
@media (prefers-reduced-motion: reduce) {
  .stage *,
  .stage {
    animation: none !important;
    transition: none !important;
  }

  /* Лента к итогам не едет, а сразу оказывается там. */
  .variantWindow {
    scroll-behavior: auto;
  }

  .stage[data-trips='leaving'] .trips {
    opacity: 0;
  }

  .stage[data-editor='entering'] .editor,
  .stage[data-fill='entering'] .card,
  .stage[data-fill='entering'] .totals,
  .stage[data-fill='filled'] .variantWin .win,
  .windowWrap,
  .window,
  .draft,
  .personNew {
    opacity: 1;
  }

  .stage[data-trips='leaving'] .tripPicked {
    border-color: var(--focus);
  }
}

/* ── Мобильный вид (порог продублирован в ui/shell/useIsMobile.ts) ───────
   Библиотека уходит совсем: на такой ширине она отняла бы у доски половину,
   а рассказ третьего и четвёртого шага — про доску. */
@media (max-width: 720px) {
  .stage {
    --tour-cards-h: 150px;

    height: 260px;
  }

  .trips {
    padding: var(--sp-5);
  }

  .lib {
    display: none;
  }

  .tripRoute {
    display: none;
  }

  /* Окно занимает почти всю сцену: поля и кнопка рядом в 360px не встают. */
  .windowWrap {
    padding: var(--sp-4);
  }

  .window {
    width: 100%;
    padding: var(--sp-4);
  }

  /* В узкой колонке бейдж не встаёт рядом с суммой и обрезается многоточием
     ровно на той цифре, ради которой он есть, — поэтому уезжает под неё.
     Лишняя строка ничего не ломает: лента доезжает до конца сама. */
  .win {
    order: 3;
    width: 100%;
    margin-top: var(--sp-2);
    text-align: center;
  }
}
